import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Psychology,
  Schedule,
  People,
  Analytics,
  Security,
  CloudSync,
  TrendingUp,
  Login,
  Favorite,
  CheckCircle,
  Support // Adicionar este ícone
} from '@mui/icons-material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
// No topo do arquivo, alterar o import:
import logoGestao from '../../assets/logo-sistema-completa.png';
// Importar componentes de diálogos legais
import { PrivacidadeDialog, TermosDialog, LGPDDialog, CookiesDialog } from './LegalDialogs';
import './LandingPage.css';
import { contatoService } from '../../services/api';

const LandingPage = () => {
  const navigate = useNavigate();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    nome: '',
    email: '',
    celular: '',
    mensagem: ''
  });
  const [enviandoContato, setEnviandoContato] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Estados para popups legais
  const [privacidadeOpen, setPrivacidadeOpen] = useState(false);
  const [termosOpen, setTermosOpen] = useState(false);
  const [lgpdOpen, setLgpdOpen] = useState(false);
  const [cookiesOpen, setCookiesOpen] = useState(false);

  const handleLogin = () => {
    navigate('/login');
  };

  const handlePlanoProfissional = () => {
    navigate('/assinar');
  };

  const handleWhatsAppPlan = (plano) => {
    const message = encodeURIComponent(`Escolhi o plano ${plano} e quero fazer minha inscrição.`);
    const url = `https://wa.me/5581999791972?text=${message}`;
    window.open(url, '_blank');
  };

  const handleOpenContactModal = () => {
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setContactForm({ nome: '', email: '', celular: '', mensagem: '' });
  };

  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitContact = async () => {
    try {
      setEnviandoContato(true);
      // validação simples
      if (!contactForm.nome?.trim() || !contactForm.email?.trim() || !contactForm.mensagem?.trim()) {
        setSnackbar({ open: true, message: 'Por favor, preencha Nome, E-mail e Mensagem.', severity: 'warning' });
        setEnviandoContato(false);
        return;
      }
      const payload = {
        nome: contactForm.nome?.trim(),
        email: contactForm.email?.trim(),
        celular: contactForm.celular?.trim() || '',
        mensagem: contactForm.mensagem?.trim()
      };
      const resp = await contatoService.enviar(payload);
      if (resp?.data?.success) {
        setSnackbar({ open: true, message: 'Mensagem enviada com sucesso! Em breve entraremos em contato.', severity: 'success' });
        handleCloseContactModal();
      } else {
        setSnackbar({ open: true, message: resp?.data?.message || 'Não foi possível enviar sua mensagem agora. Tente novamente.', severity: 'error' });
      }
    } catch (err) {
      console.error('Erro ao enviar contato:', err);
      setSnackbar({ open: true, message: 'Falha ao enviar mensagem. Verifique sua conexão e tente novamente.', severity: 'error' });
    } finally {
      setEnviandoContato(false);
    }
  };

  // Máscara/formatação para celular BR
  const formatarCelularBR = (valor) => {
    if (!valor) return '';
    // remove tudo que não é dígito e limita a 11 dígitos
    const nums = valor.replace(/\D/g, '').slice(0, 11);
    if (nums.length <= 2) return nums.replace(/(\d{0,2})/, '($1');
    if (nums.length <= 6) return nums.replace(/(\d{2})(\d{0,4})/, '($1) $2');
    if (nums.length <= 10) return nums.replace(/(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3');
    // 11 dígitos: (DD) 9 9999-9999
    return nums.replace(/(\d{2})(\d{1})(\d{4})(\d{0,4})/, '($1) $2 $3-$4');
  };

  const features = [
    {
      icon: <Psychology sx={{ fontSize: 48, color: '#8b7355' }} />,
      title: 'Gestão Completa de Pacientes',
      description: 'Prontuários eletrônicos seguros, histórico detalhado de sessões, evolução do tratamento e relatórios personalizados para cada paciente.'
    },
    {
      icon: <Schedule sx={{ fontSize: 48, color: '#8b7355' }} />,
      title: 'Agendamento Inteligente',
      description: 'Sistema de agendamento online com confirmação automática, lembretes por WhatsApp e e-mail, e sincronização com Google Calendar.'
    },
    {
      icon: <Analytics sx={{ fontSize: 48, color: '#8b7355' }} />,
      title: 'Dashboard e Relatórios',
      description: 'Métricas em tempo real, relatórios financeiros, análise de produtividade e insights para crescimento da sua prática clínica.'
    },
    {
      icon: <Security sx={{ fontSize: 48, color: '#8b7355' }} />,
      title: 'Segurança e Conformidade',
      description: 'Criptografia de ponta a ponta, backup automático, conformidade com LGPD e CFP, garantindo total proteção dos dados.'
    },
    {
      icon: <People sx={{ fontSize: 48, color: '#667eea' }} />,
      title: 'Gestão Financeira',
      description: 'Controle de receitas e despesas, emissão de recibos, relatórios fiscais e integração com sistemas de pagamento.'
    },
    {
      icon: <CloudSync sx={{ fontSize: 48, color: '#667eea' }} />,
      title: 'Acesso em Qualquer Lugar',
      description: 'Sistema 100% online, acesse de qualquer dispositivo, sincronização automática e funcionamento offline quando necessário.'
    }
  ];

  const testimonials = [
    {
      name: 'Patrícia Allgayer',
      role: 'Psicóloga Clínica - CRP 02/27100',
      avatar: '/patricia.png',
      rating: 5,
      text: 'O Trajetus revolucionou minha prática clínica. Economizo 3 horas por dia em tarefas administrativas e posso focar totalmente nos meus pacientes. O sistema é intuitivo e muito seguro.'
    }
  ];

  const stats = [
    { number: '200+', label: 'Psicólogos Ativos' },
    { number: '1.700+', label: 'Pacientes Atendidos' },
    { number: '98%', label: 'Satisfação' },
    { number: '24/7', label: 'Suporte' }
  ];



  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="header-content">
          <div className="logo-container">
            <img src="/logo_header.png" alt="Trajetus" style={{ height: 40 }} />
          </div>
          <Button 
            className="login-button-discrete"
            onClick={handleLogin}
            variant="text"
            startIcon={<Login />}
            size="small"
          >
            Entrar no Sistema
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <div className="hero-content">
                <Chip 
                  label="✨ Mais de 200 psicólogos confiam em nós" 
                  className="hero-chip"
                  color="primary"
                />
                <Typography variant="h1" component="h1" className="hero-title">
                  Plataforma Completa para
                  <span className="highlight"> Psicólogos Modernos</span>
                </Typography>
                <Typography variant="h5" component="p" className="hero-subtitle">
                  Transforme sua prática clínica com tecnologia de ponta. 
                  Gestão completa de pacientes, agendamento inteligente e relatórios profissionais.
                </Typography>
                <Box className="hero-buttons">
                  <Button 
                    className="cta-button-primary"
                    onClick={handleOpenContactModal}
                    variant="contained"
                    size="large"
                  >
                    Começar Gratuitamente
                  </Button>
                  <Button 
                    className="cta-button-secondary"
                    onClick={handleLogin}
                    variant="outlined"
                    size="large"
                  >
                    Acessar o Sistema
                  </Button>
                </Box>
                <Typography variant="body2" className="hero-note">
                  ✅ Teste grátis por 30 dias • ✅ Sem cartão de crédito • ✅ Suporte incluído
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="hero-image">
                <Grid item xs={12} md={6}>
                  <div className="hero-image">
                    <img 
                      src="/telas.png" 
                      alt="Telas do Sistema Trajetus" 
                      style={{
                        width: 'calc(100% + 200px)',
                        height: 'auto',
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                        maxWidth: '800px'
                      }}
                    />
                  </div>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <div className="stat-item">
                  <Typography variant="h3" component="div" className="stat-number">
                    {stat.number}
                  </Typography>
                  <Typography variant="body1" className="stat-label">
                    {stat.label}
                  </Typography>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Container maxWidth="lg">
          <div className="section-header">
            <Typography variant="h2" component="h2" className="section-title">
              Tudo que você precisa para uma prática de sucesso
            </Typography>
            <Typography variant="h6" className="section-subtitle">
              Ferramentas profissionais desenvolvidas especificamente para psicólogos
            </Typography>
          </div>
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card className="feature-card-modern">
                  <CardContent>
                    <div className="feature-icon-modern">
                      {feature.icon}
                    </div>
                    <Typography variant="h6" component="h3" className="feature-title">
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" className="feature-description">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <Container maxWidth="lg">
          <div className="section-header">
            <Typography variant="h2" component="h2" className="section-title">
              Nossos Valores
            </Typography>
            <Typography variant="h6" className="section-subtitle">
              Os princípios que guiam nossa missão de transformar a psicologia
            </Typography>
          </div>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card className="value-card">
                <CardContent>
                  <div className="value-icon">
                    <Security sx={{ fontSize: 48, color: '#2196f3' }} />
                  </div>
                  <Typography variant="h5" component="h3" className="value-title">
                    Segurança Total
                  </Typography>
                  <Typography variant="body1" className="value-description">
                    Garantimos a máxima proteção dos dados sensíveis, seguindo rigorosamente as normas de privacidade e ética profissional.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card className="value-card">
                <CardContent>
                  <div className="value-icon">
                    <TrendingUp sx={{ fontSize: 48, color: '#4caf50' }} />
                  </div>
                  <Typography variant="h5" component="h3" className="value-title">
                    Crescimento Contínuo
                  </Typography>
                  <Typography variant="body1" className="value-description">
                    Evoluímos constantemente nossa plataforma, incorporando as melhores práticas e inovações da área.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card className="value-card">
                <CardContent>
                  <div className="value-icon">
                    <Support sx={{ fontSize: 48, color: '#ff9800' }} />
                  </div>
                  <Typography variant="h5" component="h3" className="value-title">
                    Suporte Especializado
                  </Typography>
                  <Typography variant="body1" className="value-description">
                    Oferecemos suporte técnico 24/7 com profissionais especializados em psicologia e tecnologia para garantir sua produtividade.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </section>

        {/* Pricing Section */}
        <section className="pricing-section" id="precos">
          <Container maxWidth="lg">
            <div className="section-header">
              <Typography variant="h2" className="section-title">
                Nossos Preços
              </Typography>
              <Typography variant="h6" className="section-subtitle" style={{ marginBottom: '50px' }}>
                Transforme sua prática psicológica com nossa plataforma completa
              </Typography>
            </div>
            <Grid container spacing={4} justifyContent="center">
              {/* Plano Básico */}
              <Grid item xs={12} md={4}>
                <Card className="pricing-card">
                  <CardContent className="pricing-content">
                    <Typography variant="h5" className="pricing-plan-name">
                      Básico
                    </Typography>
                    <Typography variant="body2" className="pricing-plan-subtitle">
                      Ideal para profissionais iniciantes
                    </Typography>
                    <div className="pricing-price">
                      <Typography variant="h3" className="pricing-amount">
                        R$ 0
                      </Typography>
                      <Typography variant="body2" className="pricing-period">
                        /mês
                      </Typography>
                    </div>
                    <div className="pricing-features">
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Até 5 pacientes</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Agendamento básico</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Prontuário eletrônico</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Relatórios simples</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Suporte por email</Typography>
                      </div>
                    </div>
                    <Button 
                      variant="outlined" 
                      className="pricing-button pricing-button-basic" 
                      fullWidth
                      onClick={() => handleWhatsAppPlan('Básico')}
                    >
                      Teste Grátis por 30 dias
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Plano Profissional */}
              <Grid item xs={12} md={4}>
                <Card className="pricing-card pricing-card-popular">
                  <div className="pricing-popular-badge">
                    <Typography variant="caption">Mais Popular</Typography>
                  </div>
                  <CardContent className="pricing-content">
                    <Typography variant="h5" className="pricing-plan-name">
                      Profissional
                    </Typography>
                    <Typography variant="body2" className="pricing-plan-subtitle">
                      Para profissionais estabelecidos
                    </Typography>
                    <div className="pricing-price">
                      <Typography variant="h3" className="pricing-amount">
                        R$ 59,99
                      </Typography>
                      <Typography variant="body2" className="pricing-period">
                        /mês
                      </Typography>
                    </div>
                    <div className="pricing-features">
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Até 60 pacientes</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Agendamento avançado</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Prontuário completo</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Relatórios avançados</Typography>
                      </div>
                      {/* Linha 438 removida: // Removido: API personalizada */}
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Suporte 24/7 (Email e Whatsapp)</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Suporte prioritário</Typography>
                      </div>
                    </div>
                    <Button 
                      variant="contained" 
                      className="pricing-button pricing-button-professional" 
                      fullWidth
                      onClick={handlePlanoProfissional}
                    >
                      Escolher Plano
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Plano Premium */}
              <Grid item xs={12} md={4}>
                <Card className="pricing-card">
                  <CardContent className="pricing-content">
                    <Typography variant="h5" className="pricing-plan-name">
                      Premium
                    </Typography>
                    <Typography variant="body2" className="pricing-plan-subtitle">
                      Para clínicas e consultórios
                    </Typography>
                    <div className="pricing-price">
                      <Typography variant="h3" className="pricing-amount">
                        R$ 499,99
                      </Typography>
                      <Typography variant="body2" className="pricing-period">
                        /mês
                      </Typography>
                    </div>
                    <div className="pricing-features">
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Pacientes ilimitados</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Até 10 Psicólogos</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Gestão completa</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Analytics avançados</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Suporte 24/7 (Prioritário)</Typography>
                      </div>
                      <div className="pricing-feature">
                        <CheckCircle className="pricing-feature-icon" />
                        <Typography variant="body2">Link de acompanhamento diário de Paciente</Typography>
                      </div>
                    </div>
                    <Button 
                      variant="outlined" 
                      className="pricing-button pricing-button-premium" 
                      fullWidth
                      onClick={() => handleWhatsAppPlan('Premium')}
                    >
                      Falar com Vendas
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box className="pricing-guarantee">
              <Typography variant="body1" className="pricing-guarantee-text">
                ✅ Teste grátis por 30 dias • ✅ Sem compromisso • ✅ Cancele quando quiser
              </Typography>
            </Box>
          </Container>
        </section>

        {/* Benefits Section */}
        <section className="benefits-section">
          <Container maxWidth="lg">
            <div className="section-header">
              <Typography variant="h2" className="section-title">
                Por que escolher o Trajetus?
              </Typography>
              <Typography variant="h6" className="section-subtitle">
                Transforme sua prática psicológica com nossa plataforma completa
              </Typography>
            </div>
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className="benefits-list">
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Interface intuitiva e fácil de usar
                    </Typography>
                  </div>
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Segurança total dos dados dos pacientes
                    </Typography>
                  </div>
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Relatórios detalhados e insights valiosos
                    </Typography>
                  </div>
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Suporte técnico especializado 24/7
                    </Typography>
                  </div>
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Economia de até 70% do tempo administrativo
                    </Typography>
                  </div>
                  <div className="benefit-item">
                    <CheckCircle className="benefit-icon" />
                    <Typography variant="body1">
                      Conformidade total com LGPD e CFP
                    </Typography>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div className="benefits-image">
                  <div className="floating-card">
                    <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
                      Economia de Tempo
                    </Typography>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      Até 70% menos tempo em tarefas administrativas
                    </Typography>
                  </div>
                  <div className="floating-card">
                    <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '0.5rem' }}>
                      Satisfação do Cliente
                    </Typography>
                    <Typography variant="body2" style={{ color: '#64748b' }}>
                      98% de aprovação dos profissionais
                    </Typography>
                  </div>
                </div>
              </Grid>
            </Grid>
          </Container>
        </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <Container maxWidth="lg">
          <div className="section-header">
            <Typography variant="h2" component="h2" className="section-title">
              O que nossos usuários dizem
            </Typography>
            <Typography variant="h6" className="section-subtitle">
              Depoimentos reais de psicólogos que transformaram sua prática
            </Typography>
          </div>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card className="testimonial-card">
                  <CardContent>
                    <div className="testimonial-header">
                      <Avatar
                        src={testimonial.avatar}
                        alt={testimonial.name}
                        sx={{
                          width: 60,
                          height: 60,
                          border: '3px solid #fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          fontSize: '1.2rem',
                          fontWeight: 600,
                          bgcolor: '#8b7355'
                        }}
                      >
                        {testimonial.name.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <div>
                        <Typography variant="h6" className="testimonial-name">
                          {testimonial.name}
                        </Typography>
                        <Typography variant="body2" className="testimonial-role">
                          {testimonial.role}
                        </Typography>
                        <Rating value={testimonial.rating} readOnly size="small" />
                      </div>
                    </div>
                    <Typography variant="body1" className="testimonial-text">
                      "{testimonial.text}"
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Container maxWidth="md">
          <div className="cta-content">
            <Typography variant="h2" component="h2" className="cta-title">
              Pronto para transformar sua prática?
            </Typography>
            <Typography variant="h6" className="cta-subtitle">
              Junte-se a mais de 200 psicólogos que já revolucionaram seu atendimento
            </Typography>
            <Box className="cta-buttons">
              <Button 
                className="cta-button-primary"
                onClick={handleOpenContactModal}
                variant="contained"
                size="large"
              >
                Começar Agora - Grátis
              </Button>
              <Button
                className="cta-button-secondary"
                component="a"
                href="https://wa.me/5581999791972"
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="large"
                startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                sx={{ ml: 2, borderColor: '#8b7355', color: '#8b7355', '&:hover': { borderColor: '#7a644a', bgcolor: 'rgba(139, 115, 85, 0.08)' } }}
                title="Falar no WhatsApp"
              >
                Falar no WhatsApp
              </Button>
            </Box>
            <Typography variant="body2" className="cta-note">
              30 dias grátis • Sem compromisso • Cancele quando quiser
            </Typography>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <div className="footer-brand">
                <img src={logoGestao} alt="Gestão PSI" className="footer-logo" />
                <Typography variant="body2" className="footer-description">
                  A plataforma completa para gestão de clínicas de psicologia. 
                  Tecnologia a serviço do cuidado humano.
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={8}>
              <Grid container spacing={4}>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" className="footer-title">Produto</Typography>
                  <ul className="footer-links">
                    <li>Funcionalidades</li>
                    <li>Preços</li>
                    <li>Segurança</li>
                    <li>Integrações</li>
                  </ul>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" className="footer-title">Suporte</Typography>
                  <ul className="footer-links">
                    <li>Central de Ajuda</li>
                    <li>Treinamentos</li>
                    <li
                      onClick={handleOpenContactModal}
                      style={{ cursor: 'pointer', color: '#8b7355', textDecoration: 'underline' }}
                      title="Fale com a Trajetus"
                    >
                      Contato
                    </li>
                    <li>Status</li>
                  </ul>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" className="footer-title">Empresa</Typography>
                  <ul className="footer-links">
                    <li>Sobre Nós</li>
                    <li>Blog</li>
                    <li>Carreiras</li>
                    <li>Imprensa</li>
                  </ul>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="h6" className="footer-title">Legal</Typography>
                  <ul className="footer-links">
                    <li onClick={() => setPrivacidadeOpen(true)} style={{ cursor: 'pointer' }}>Privacidade</li>
                    <li onClick={() => setTermosOpen(true)} style={{ cursor: 'pointer' }}>Termos</li>
                    <li onClick={() => setLgpdOpen(true)} style={{ cursor: 'pointer' }}>LGPD</li>
                    <li onClick={() => setCookiesOpen(true)} style={{ cursor: 'pointer' }}>Cookies</li>
                  </ul>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          <div className="footer-bottom">
            <Typography variant="body2" className="footer-copyright">
              2024 Trajetus. Todos os direitos reservados.
            </Typography>
            <div className="footer-social">
              <Typography variant="body2">
                Desenvolvido com para psicólogos brasileiros
              </Typography>
            </div>
          </div>
        </Container>
      </footer>
      
      {/* Modal de Contato */}
      <Dialog
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 3,
            backgroundColor: '#f7f2ea'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#efe6d9', color: '#5c4a3a' }}>
          <img src="/logo-sistema-icone.png" alt="Trajetus" style={{ width: 28, height: 28 }} />
          <span>Fale com a Trajetus</span>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome *"
              value={contactForm.nome}
              onChange={(e) => handleContactFormChange('nome', e.target.value)}
              margin="normal"
              InputLabelProps={{ sx: { color: '#5c4a3a' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d3c4af' },
                  '&:hover fieldset': { borderColor: '#b89b74' },
                }
              }}
            />
            <TextField
              fullWidth
              label="E-mail *"
              type="email"
              value={contactForm.email}
              onChange={(e) => handleContactFormChange('email', e.target.value)}
              margin="normal"
              InputLabelProps={{ sx: { color: '#5c4a3a' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d3c4af' },
                  '&:hover fieldset': { borderColor: '#b89b74' },
                }
              }}
            />
            <TextField
              fullWidth
              label="Celular"
              value={contactForm.celular}
              onChange={(e) => handleContactFormChange('celular', formatarCelularBR(e.target.value))}
              margin="normal"
              placeholder="(00) 9 0000-0000"
              inputMode="tel"
              inputProps={{ maxLength: 17, pattern: "\\(\\d{2}\\)\\s?(?:\\d\\s)?\\d{4}-\\d{4}" }}
              InputLabelProps={{ sx: { color: '#5c4a3a' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d3c4af' },
                  '&:hover fieldset': { borderColor: '#b89b74' },
                }
              }}
            />
            <TextField
              fullWidth
              label="Mensagem *"
              multiline
              rows={4}
              value={contactForm.mensagem}
              onChange={(e) => handleContactFormChange('mensagem', e.target.value)}
              margin="normal"
              InputLabelProps={{ sx: { color: '#5c4a3a' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#d3c4af' },
                  '&:hover fieldset': { borderColor: '#b89b74' },
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseContactModal} sx={{ color: '#5c4a3a' }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmitContact}
            disabled={enviandoContato}
            sx={{
              bgcolor: '#8b7355',
              '&:hover': { bgcolor: '#7a644a' }
            }}
          >
            {enviandoContato ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LandingPage;
